/**
 * Shared logic factory for useMagicSignLogic.tsx (company-scoped) and
 * useGlobalMagicSignLogic.ts (global view): fetchConfigMeta was byte-identical between the two,
 * and handleGenerateLinks differed only in how the raw response is mapped and merged back into
 * state. Both now call this hook once instead of maintaining two copies that could silently
 * diverge on the next fix (retry, new step, error copy).
 *
 * Pattern reference: useMagicSignCommonHandlers.ts (same shared-hook-between-both-views pattern,
 * for the row-action handlers).
 */
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { ConfigMeta, MagicSignFilters, MagicSignRow, MagicSignStatus } from './types';
import { fetchMagicSignConfigMeta, generateMagicSignLinks } from './MagicSignService';
import { mapConfigMetaRaw, deriveSigningLinks } from './utils';
import { fetchDocList } from '../MagicUpload/MagicUploadService';

interface UseMagicSignSharedOptions<TRow extends MagicSignRow> {
    pnoId: string;
    setRows: React.Dispatch<React.SetStateAction<TRow[]>>;
    fetchList: (page?: number, filters?: MagicSignFilters) => Promise<void>;
    activeFilters: MagicSignFilters;
    /**
     * Maps the raw generateLinks response before deriveSigningLinks runs. The global view uses
     * this to fold the backend's `organizationName` into `orgName`; the company view can omit it.
     */
    mapRawRow?: (raw: any) => TRow;
    /**
     * Merges the freshly-generated row back into the row already in state. The global view uses
     * this to preserve org fields the generateLinks DTO doesn't carry; the company view can omit
     * it (the updated row fully replaces the previous one).
     */
    mergeRow?: (updatedRow: TRow, previousRow: TRow) => TRow;
}

const useMagicSignShared = <TRow extends MagicSignRow>({
    pnoId,
    setRows,
    fetchList,
    activeFilters,
    mapRawRow,
    mergeRow,
}: UseMagicSignSharedOptions<TRow>) => {
    const [configMeta, setConfigMeta] = useState<ConfigMeta | null>(null);

    const fetchConfigMeta = useCallback(async () => {
        if (!pnoId) return;
        try {
            const res = await fetchMagicSignConfigMeta(pnoId);
            const raw = res?.data?.data;
            setConfigMeta(raw ? mapConfigMetaRaw(raw) : null);
        } catch (error) {
            // Non-fatal: fall back to defaults in constants.ts
            console.error('[MagicSign] Failed to fetch config meta', error);
        }
    }, [pnoId]);

    const handleGenerateLinks = useCallback(
        async (id: string): Promise<TRow | null> => {
            // Optimistically flip to the transient "Generating Links" state (badge + spinner) so
            // the user gets immediate feedback while the request is in flight.
            setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: MagicSignStatus.GENERATING_LINKS } : r)));
            try {
                const res = await generateMagicSignLinks({ id, origin: window.location.origin });
                const raw = res?.data?.data;
                if (!raw) {
                    // Envelope succeeded (responseCode===SUCCESS) but carried no row data — this
                    // should not happen, but without this guard the row stays stuck in the
                    // optimistic GENERATING_LINKS state forever with no feedback to the user.
                    console.error('[MagicSign] generateMagicSignLinks succeeded but returned no data');
                    toast.error('Failed to generate signing links.');
                    fetchList(0, activeFilters);
                    return null;
                }
                const updatedRow = deriveSigningLinks((mapRawRow ? mapRawRow(raw) : raw) as TRow);
                let merged: TRow = updatedRow;
                setRows((prev) =>
                    prev.map((r) => {
                        if (r.id !== id) return r;
                        merged = mergeRow ? mergeRow(updatedRow, r) : updatedRow;
                        return merged;
                    }),
                );
                return merged;
            } catch (error) {
                console.error('[MagicSign] Failed to generate signing links', error);
                toast.error(error instanceof Error && error.message ? error.message : 'Failed to generate signing links.');
                fetchList(0, activeFilters);
                return null;
            }
        },
        [fetchList, activeFilters, setRows, mapRawRow, mergeRow],
    );

    // Document Type uses the SAME per-PNO Polus doc-type list as Magic Upload's Data Type column,
    // fetched once here (per hook instance, i.e. once per table) instead of inside MagicSignTable —
    // keeps the table presentational and gives both views (company + global) the same list.
    const [polusDocTypes, setPolusDocTypes] = useState<string[]>([]);
    const fetchPolusDocTypes = useCallback(async () => {
        if (!pnoId) return;
        try {
            const res = await fetchDocList({ pnoId });
            setPolusDocTypes(Array.isArray(res?.data?.documentList) ? res.data.documentList : []);
        } catch (error) {
            // Non-fatal — MagicSignTable falls back to configMeta.documentTypes.
            console.error('[MagicSign] Failed to fetch Polus doc types', error);
        }
    }, [pnoId]);

    return { configMeta, fetchConfigMeta, handleGenerateLinks, polusDocTypes, fetchPolusDocTypes };
};

export default useMagicSignShared;
